import { memo, useEffect } from 'react'
import type { Location } from '../../data/locations.tsx'
import { useRenderedLocations } from '../../states.ts'
import { LocationLayer } from './LocationLayer.tsx'
import { LocationMarker } from './LocationMarker.tsx'
import { useFilteredLocations } from './locationUtils.ts'

export const LocationMarkers = ({
	selectedLocations = [],
	interactive,
	locations: customLocations,
	onLocationSelect,
	onMapClick,
}: {
	selectedLocations?: Location[]
	interactive?: boolean
	locations?: Location[]
	onLocationSelect?: (location: Location) => void
	onMapClick?: (location: { lat: number; lon: number }) => void
}) => {
	const { locations: allLocations } = useFilteredLocations()
	const locations = customLocations ?? allLocations
	const { locationIds, add, remove } = useRenderedLocations()
	useEffect(() => {
		for (const { id } of selectedLocations) add(id)
	}, [selectedLocations, add])

	return (
		<>
			<LocationLayer locations={locations} onMapClick={onMapClick} />
			{locations
				.filter((loc) => locationIds.has(loc.id))
				.map((loc) => (
					<RenderedLocationMarker
						key={loc.id}
						location={loc}
						selected={selectedLocations.some(({ id }) => id === loc.id)}
						interactive={interactive}
						onSelect={onLocationSelect}
						onRemove={remove}
					/>
				))}
		</>
	)
}

const RenderedLocationMarker = memo(
	({
		location,
		selected,
		interactive,
		onSelect,
		onRemove,
	}: {
		location: Location
		selected: boolean
		interactive?: boolean
		onSelect?: (location: Location) => void
		onRemove: (locationId: string) => void
	}) => (
		<LocationMarker
			id={location.id}
			icon={location.icon}
			name={location.name}
			longitude={location.lon}
			latitude={location.lat}
			location={location}
			selected={selected}
			onClick={onSelect && (() => onSelect(location))}
			collapseProps={{ onExited: () => onRemove(location.id) }}
			interactive={interactive}
		/>
	),
)
